import type { ReactNode } from 'react';
import { AppRecordPanel } from '@/components/apps/AppRecordPanel';
import { AppRecordReview } from '@/components/apps/AppRecordReview';
import type { AppData } from '@/components/apps/useAppData';
import { AttachRecordControl } from '@/components/apps/AttachRecordControl';
import { recordTitle } from '@/components/apps/app-records-model';
import { type AppCollection, type AppRecord, type AppRecordChanges, archiveAppRecord, attachAppRecord, undoAppRecordEvent, updateAppRecord } from '@/lib/app-records';
import { createAppLabel, setAppRecordLabel } from '@/lib/app-collaboration';

interface AppRecordFicheProps {
  appId: string;
  data: AppData;
  collection: AppCollection;
  record: AppRecord;
  canManage: boolean;
  listColumnKeys?: string[];
  next?: ReactNode;
  tools?: ReactNode;
  lead?: ReactNode;
  account?: ReactNode;
  onOpenRun?: (runId: string) => void;
  /** After « Rattacher à… »: this object is gone, the kept one opens. */
  onAttached?: (keptRecordId: string) => void;
}

/** One object with everything people do on it: status, review, labels, folders, comments, history. */
export const AppRecordFiche = ({ appId, data, collection, record, canManage, listColumnKeys, next, tools, lead, account, onOpenRun, onAttached }: AppRecordFicheProps) => (
  <AppRecordPanel record={record} collection={collection} members={data.members} canManage={canManage} listColumnKeys={listColumnKeys} next={next} tools={tools} lead={lead} account={account}
    attach={collection.mode === 'rows' && onAttached ? (
      <AttachRecordControl title={recordTitle(record, collection)}
        candidates={(data.list?.records ?? []).filter((item) => item.id !== record.id && !item.archivedAt)
          .map((item) => ({ id: item.id, title: recordTitle(item, collection) }))}
        onAttach={async (intoRecordId) => {
          const kept = await attachAppRecord(record, intoRecordId);
          void data.reload();
          onAttached(kept.id);
        }} />
    ) : undefined}
    review={collection.reviewGrid ? (
      <AppRecordReview record={record} grid={collection.reviewGrid} members={data.members} userId={data.userId}
        reviews={data.reviews.filter((review) => review.recordId === record.id)}
        onSaved={() => { data.reloadReviews(); data.bumpHistory(); }} />
    ) : undefined}
    collaboration={{
      appId, labels: data.labels.labels, selectedLabels: data.labels.byRecord[record.id] ?? [], teams: data.teams,
      canCreateLabel: data.labels.policy === 'members' || canManage,
      onToggleLabel: async (labelId, on) => {
        try { await setAppRecordLabel(record.id, labelId, on); } catch (cause) { data.setError(cause instanceof Error ? cause.message : 'Étiquette non modifiée.'); }
        data.reloadLabels(); data.bumpHistory();
      },
      onCreateLabel: async (name, tone) => {
        try { await createAppLabel(appId, name, tone); } catch (cause) { data.setError(cause instanceof Error ? cause.message : 'Étiquette non créée.'); }
        data.reloadLabels();
      },
      onCommented: data.bumpHistory,
      historyKey: data.historyKey,
    }}
    onChange={(changes: AppRecordChanges) => data.run(() => updateAppRecord(record, changes))}
    onArchive={(archived) => data.run(() => archiveAppRecord(record, archived))}
    onUndo={(eventId) => data.run(() => undoAppRecordEvent(eventId))}
    onOpenRun={onOpenRun} />
);
